import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const AGREEMENTS = [
  {
    key: "ncnda_consent",
    title: "Master NCNDA",
    full: "Non-Circumvention, Non-Disclosure Agreement (supplier-side)",
    body: "I acknowledge that any sanitized buyer request or buyer identity revealed to me through Campbell's Commodities Brokerage LLC is confidential. I will not circumvent the brokerage, contact the buyer directly, or transact outside the platform for 5 years. Violation results in liability for all projected commissions plus damages.",
    testid: "agree-ncnda",
  },
  {
    key: "commission_agreement_consent",
    title: "Commission Agreement",
    full: "Brokerage Commission & IMFPA Acknowledgement",
    body: "I acknowledge that Campbell's Commodities Brokerage LLC is paid by Irrevocable Master Fee Protection Agreement (IMFPA) lodged at the transaction banks. Commission is cut off the top of every Letter of Credit before I receive my net payout, at the rates published openly in the brokerage's commission schedule. I will not attempt to alter, dispute or reduce this allocation post-contract.",
    testid: "agree-commission",
  },
  {
    key: "aml_attestation_consent",
    title: "AML / Source-of-Funds Attestation",
    full: "Anti-Money-Laundering & Source-of-Goods Attestation",
    body: "I attest that the commodities I supply are sourced lawfully; that I am not subject to OFAC, UN, EU or UK sanctions; that no beneficial owner is a Politically Exposed Person whose involvement has not been disclosed; and that I will promptly notify the brokerage of any change in these representations.",
    testid: "agree-aml",
  },
  {
    key: "code_of_conduct_consent",
    title: "Code of Conduct",
    full: "Supplier Code of Conduct",
    body: "I will honor every FCO I issue through the platform, deliver against the volume and specs in the executed SPA, refrain from misrepresenting origin or quality, and respond to brokerage communications within two business days. Repeated breaches result in removal from the supplier network.",
    testid: "agree-conduct",
  },
];

export default function SupplierAgreements() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const existing = user?.supplier_agreements;
  const alreadySigned = !!existing?.ncnda_signed_at;
  const [consents, setConsents] = useState(() =>
    AGREEMENTS.reduce((acc, a) => ({ ...acc, [a.key]: alreadySigned }), {})
  );
  const [sig, setSig] = useState(existing?.signature_full_name || user?.name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const allChecked = AGREEMENTS.every((a) => consents[a.key]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/me/supplier-agreements/sign", {
        ...consents,
        signature_full_name: sig.trim(),
      });
      await refresh();
      nav("/dashboard/supplier");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Sign failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout testid="supplier-agreements-page">
      <form onSubmit={submit} className="p-8 md:p-12 max-w-3xl">
        <div className="overline">Supplier desk · step 04</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Sign your supplier agreements</h1>
        <p className="text-sub mt-4 leading-relaxed">
          Four standing agreements. They apply to every deal you&apos;re shown
          through this platform — you sign once, not deal-by-deal.
          {alreadySigned && (
            <span className="block mt-2 text-success">
              ✓ Already on file ({existing.code_of_conduct_signed_at?.slice(0, 19).replace("T", " ")}). Re-signing replaces the record.
            </span>
          )}
        </p>

        <div className="mt-10 space-y-6">
          {AGREEMENTS.map((a, i) => (
            <label key={a.key}
              className={`block border ${consents[a.key] ? "border-success" : "border-line"} bg-parchment p-6 cursor-pointer`}
              data-testid={a.testid}>
              <div className="flex items-baseline justify-between gap-4">
                <div>
                  <div className="overline">Tier {i + 1}</div>
                  <h3 className="h-serif text-2xl text-ink mt-1">{a.title}</h3>
                  <div className="text-xs font-mono text-sub uppercase tracking-wider mt-1">{a.full}</div>
                </div>
                <input type="checkbox" className="w-5 h-5 accent-oxblood mt-1 shrink-0"
                       checked={!!consents[a.key]}
                       onChange={(e) => setConsents({ ...consents, [a.key]: e.target.checked })}
                       data-testid={`${a.testid}-check`} />
              </div>
              <p className="text-sub text-sm mt-4 leading-relaxed">{a.body}</p>
              <div className="text-xs font-mono text-sub mt-4 uppercase tracking-wider">
                {consents[a.key] ? "✓ Accepted" : "Tick to accept"}
              </div>
            </label>
          ))}
        </div>

        <section className="mt-10 border border-line bg-parchment p-6">
          <div className="overline">Typed signature</div>
          <p className="text-sub text-sm mt-2 leading-relaxed">
            Type your full legal name below. Your IP address and a timestamp are
            recorded with the four consents above for audit purposes.
          </p>
          <input type="text" className="input-line mt-4" required minLength={3}
                 value={sig} onChange={(e) => setSig(e.target.value)}
                 data-testid="agree-signature"
                 placeholder="e.g. Marco DeRossi" />
        </section>

        {err && <div className="mt-6 border-l-2 border-danger pl-3 text-sm text-danger" data-testid="agree-error">{err}</div>}

        <div className="mt-10 flex items-center gap-5">
          <button type="submit" disabled={!allChecked || busy || sig.trim().length < 3}
                  className="btn-primary text-sm" data-testid="agree-submit">
            {busy ? "Recording…" : "Sign all four agreements"}
          </button>
          {!allChecked && (
            <div className="text-xs font-mono text-sub uppercase tracking-wider">
              All four must be accepted
            </div>
          )}
        </div>
      </form>
    </DashboardLayout>
  );
}
